/**
 * Chart Utilities
 * Contains shared functions for chart components
 */

// Determines the appropriate step size based on the data range
export const determineStepSize = (dataMaxValue: number): number => {
  // Choose an appropriate step size based on the magnitude of the data
  if (dataMaxValue > 1000) {
    return 500;
  } else if (dataMaxValue > 500) {
    return 100;
  } else if (dataMaxValue > 100) {
    return 50;
  } else if (dataMaxValue > 50) {
    return 10;
  } else if (dataMaxValue > 20) {
    return 5;
  } else if (dataMaxValue > 10) {
    return 2;
  } else {
    return 1;
  }
};

// Round up the maximum value to a nice round number based on the step size
export const roundToNiceMaxValue = (dataMaxValue: number): number => {
  const step = determineStepSize(dataMaxValue);
  return Math.ceil(dataMaxValue / step) * step;
};

// Generate ticks with consistent steps (1, 2, 5, 10, etc.) based on data range
export const generateConsistentTicks = (
  dataMaxValue: number, 
  targetCount: number = 5
): number[] => {
  // Always start at 0 for charts
  const min = 0;
  
  // Round up the max value to a nice round number
  const max = roundToNiceMaxValue(dataMaxValue);
  
  // Determine the step size based on the rounded max value
  const step = determineStepSize(max);
  
  // Generate ticks based on the calculated step
  const ticks: number[] = [];
  let currentTick = min;
  
  // Prevent infinite ticks by setting a reasonable limit
  const maxTickCount = 100;
  let tickCount = 0;
  
  while (currentTick <= max && tickCount < maxTickCount) {
    ticks.push(currentTick);
    currentTick += step;
    tickCount++;
  }
  
  // Safety check - if we've generated too many ticks, limit to a reasonable number
  if (ticks.length > 10) {
    const limitedTicks: number[] = [];
    const skipFactor = Math.ceil(ticks.length / targetCount);
    
    for (let i = 0; i < ticks.length; i += skipFactor) {
      if (i < ticks.length) {
        limitedTicks.push(ticks[i]);
      }
    }
    
    // Ensure we have the last tick
    if (limitedTicks[limitedTicks.length - 1] < max) {
      limitedTicks.push(max);
    }
    
    return limitedTicks;
  }
  
  return ticks;
};